import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getSEOData } from '../utils/seoData';
import { CLINICAL_DISCLAIMER } from '../data/ewm';
import { APPLICATIONS } from '../data/applications';
import { getProducts } from '../api/api';
import './Narrative.css';

/**
 * Section 3 of the site — Applications.
 *
 * Where the memory layer lands, organised by industry. These are applicability
 * statements; the measured evidence behind them lives in the scenario pages.
 *
 * The page closes on the product catalogue: Products is not in the top bar, so
 * this section hands the reader to it, and the list is fetched so the catalogue
 * stays single-sourced with /products. If the API is unavailable the section
 * still renders with its link.
 */
function Applications() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getProducts()
      .then((res) => {
        if (!cancelled) setProducts(res.data || []);
      })
      .catch(() => {
        // Deliberately silent: the closing section degrades to its link.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="np-page">
      <SEO {...getSEOData('applications')} />

      {/* Hero */}
      <section className="np-hero">
        <div className="container">
          <span className="np-eyebrow">Applications</span>
          <h1>Where the memory layer lands, industry by industry</h1>
          <p className="np-hero-sub">
            A structural memory beside a model is useful wherever three things are true at once:
            the data cannot leave the building, the history matters more than the moment, and
            somebody will later have to check what the system knew.
          </p>
          <div className="np-cta-row np-cta-row--center">
            <a href="#verticals" className="np-btn np-btn--primary">Browse the industries</a>
            <Link to="/scenarios" className="np-btn np-btn--ghost">See the measured scenarios</Link>
          </div>
        </div>
      </section>

      {/* How to read these pages */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">How to read this section</h2>
          <p className="np-lead">
            Scenarios are what has been built and measured. Applications are where that work
            applies. The two are kept separate on purpose, because the second does not get to
            borrow the credibility of the first.
          </p>
          <div className="np-grid np-grid--2">
            <div className="np-card">
              <h3 className="np-h3">Scenarios carry the evidence</h3>
              <p className="np-body">
                Every figure on a scenario page comes from a named notebook run, and each page
                states where the result is weaker than it appears. Nothing on this site is a
                projection dressed as a result.
              </p>
            </div>
            <div className="np-card">
              <h3 className="np-h3">Applications carry the fit</h3>
              <p className="np-body">
                Each industry page states the constraint that makes memory the binding problem
                there, what the layer provides against it, and which measured scenarios would be
                deployed. No industry deployment has been run, so no industry performance number is
                published.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Verticals */}
      <section className="np-section np-section--alt" id="verticals">
        <div className="container">
          <h2 className="np-h2">Seven industries</h2>
          <p className="np-lead">
            The same layer, read against different constraints. The constraint is what changes —
            not the mechanism.
          </p>
          <div className="np-grid np-grid--3">
            {APPLICATIONS.map((a) => (
              <Link key={a.id} to={`/applications/${a.id}`} className="np-card np-card--link">
                <div className="np-card-icon">{a.icon}</div>
                <h3 className="np-h3">{a.name}</h3>
                <p className="np-body">{a.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Where the constraint is the same */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">One constraint, expressed seven ways</h2>
          <ul className="np-list">
            <li>
              <strong>The data cannot leave.</strong> Healthcare records, privileged legal material,
              claims files, classified material and regulated banking data all rule out hosted
              inference, which is usually where the convenient memory lives.
            </li>
            <li>
              <strong>The history matters more than the moment.</strong> A claim, a matter, a patient
              record and a research programme are all read repeatedly over months, and the value is
              in what changed between readings.
            </li>
            <li>
              <strong>Someone will check afterwards.</strong> Model risk governance, accreditation,
              clinical review and research reproduction all require the state to be reconstructable —
              which is exactly what a summary destroys.
            </li>
            <li>
              <strong>The hardware is already decided.</strong> None of these sectors can assume a new
              accelerator class arrives with the budget for it, so the layer has to fit the estate
              that exists.
            </li>
          </ul>

          <div className="np-caveat">
            <h3 className="np-h3">What is not claimed here</h3>
            <p className="np-body">
              No industry deployment has been run, no certification is claimed, and nothing on these
              pages is legal, clinical or financial advice. The pages state where a measured
              capability applies and which scenario backs it; the sector rules remain the
              customer&rsquo;s framework to satisfy.
            </p>
          </div>

          <div className="np-disclaimer" style={{ marginTop: '26px' }}>
            {CLINICAL_DISCLAIMER}
          </div>

          <div className="np-cta-row np-cta-row--center">
            <Link to="/partners" className="np-btn np-btn--primary">Become a design partner</Link>
            <Link to="/contact" className="np-btn np-btn--secondary">Talk to us about your constraint</Link>
          </div>
        </div>
      </section>

      {/* Closing catalogue — the applications are delivered as products. */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">The products behind these applications</h2>
          <p className="np-lead">
            Each industry page above describes where the memory layer applies. The products are
            how that work is delivered — the catalogue is where each one is specified, with its
            features and its deployment shape.
          </p>

          {products.length > 0 && (
            <div className="np-grid np-grid--3">
              {products.map((p) => (
                <Link key={p.id} to="/products" className="np-card np-card--link">
                  <div className="np-card-icon">{p.logo}</div>
                  <h3 className="np-h3">{p.name}</h3>
                  <p className="np-body">{p.description}</p>
                </Link>
              ))}
            </div>
          )}

          <div className="np-cta-row np-cta-row--center">
            <Link to="/products" className="np-btn np-btn--primary">See the full catalogue</Link>
            <Link to="/scenarios" className="np-btn np-btn--secondary">The measured scenarios</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Applications;
